<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { ref, watch, inject } from "vue"
import { useDebounceFn } from "@vueuse/core"
import { useI18n } from "vue-i18n"
import { useToast } from "primevue/usetoast"
import type { Building } from "@types"
import type { Services } from "@services"
import { useBuildingStore } from "@core/stores/building-v2.store"
import { useFetchWrapper } from "@core/composables"
import { ServiceError } from "@core/exceptions"
import type { ItemBuildingData } from "@core/components/item/ItemBuilding.vue"

defineOptions({
    name: "BuildingRelationParentDialog",
})

// ================================
// PROPS & EMITS
// ================================
const props = defineProps<{
    visible: boolean
    /** Bâtiment courant (enfant) */
    buildingId: string
    niveau: string
}>()

const emit = defineEmits<{
    "update:visible": [value: boolean]
}>()

const { t } = useI18n({
    messages: {
        fr: {
            title: "Rechercher un bâtiment à associer",
            radius: "Périmètre de recherche (m)",
            loading: "Recherche en cours…",
            loadingDone: "Sélectionnez un bâtiment",
            empty: "Aucun bâtiment à proximité",
            warning:
                "Le rattachement modifie la hiérarchie : vérifiez avant de valider.",
            add: "Ajouter",
            cancel: "Annuler",
            success: "Bâtiment parent associé",
            error: "Impossible d’associer le bâtiment parent",
        },
    },
})

const services = inject<Services>("services")!
const buildingStore = useBuildingStore()
const toast = useToast()

const radius = ref(500)
const buildings = ref<Building[]>([])
const selectedId = ref("")
const errorMessage = ref("")
const loadingConfirm = ref(false)

const { fetch: loadNear, isFetching: pendingNear } = useFetchWrapper(
    async () => {
        if (!props.visible || !props.buildingId) return
        const list = await services.$getBuildingsNear(
            props.buildingId,
            radius.value
        )
        buildings.value = (list ?? []).filter(
            (b) => (b._id ?? b._id_building) !== props.buildingId
        )
        if (
            selectedId.value &&
            !buildings.value.some((b) => getBid(b) === selectedId.value)
        ) {
            selectedId.value = ""
        }
    }
)

/** Évite une rafale d’appels API pendant le déplacement du curseur de rayon. */
const debouncedLoadNear = useDebounceFn(() => {
    if (props.visible) void loadNear()
}, 400)

function getBid(b: Building): string {
    return b._id_building ?? b._id
}

/**
 * Adapte un `Building` API au format attendu par `ItemBuilding`.
 */
function toItemBuildingData(b: Building): ItemBuildingData {
    const id = getBid(b)
    return {
        _id: id,
        name: b.name,
        niveau: b.infos?.niveau,
        address: b.address,
        tags: b.tags?.map((tag) => tag.name),
        visible_to: b.visible_to,
    }
}

watch(
    () => [props.visible, props.buildingId] as const,
    ([vis]) => {
        if (vis) {
            selectedId.value = ""
            errorMessage.value = ""
            void loadNear()
        }
    }
)

watch(radius, () => {
    if (props.visible) debouncedLoadNear()
})

function onSelectRow(id: string, selected: boolean): void {
    if (selected) {
        selectedId.value = id
    } else if (selectedId.value === id) {
        selectedId.value = ""
    }
}

function onToggleItemBuilding(b: Building): void {
    const id = getBid(b)
    onSelectRow(id, selectedId.value !== id)
}

function onClose(): void {
    emit("update:visible", false)
    radius.value = 500
    selectedId.value = ""
    buildings.value = []
    errorMessage.value = ""
}

async function onConfirm(): Promise<void> {
    if (!selectedId.value) return
    try {
        loadingConfirm.value = true
        errorMessage.value = ""
        await buildingStore.addBuildingChildren(selectedId.value, [
            props.buildingId,
        ])
        toast.add({ severity: "success", summary: t("success"), life: 3000 })
        onClose()
    } catch (err) {
        errorMessage.value =
            err instanceof ServiceError ? err.message : t("error")
        toast.add({
            severity: "error",
            summary: errorMessage.value,
            life: 5000,
        })
    } finally {
        loadingConfirm.value = false
    }
}
</script>

<template>
    <BaseDialog
        :visible="visible"
        modal
        :header="t('title')"
        class="w-full max-w-2xl"
        @update:visible="emit('update:visible', $event)"
    >
        <div class="flex flex-col gap-4">
            <div>
                <label class="text-sm text-surface-700 block mb-2">{{
                    t("radius")
                }}</label>
                <BaseSlider
                    v-model="radius"
                    :min="0"
                    :max="10000"
                    class="w-full"
                />
                <p class="text-center text-sm mt-1">{{ radius }} m</p>
            </div>
            <p class="text-sm text-center text-surface-600">
                {{ pendingNear ? t("loading") : t("loadingDone") }}
            </p>
            <div v-if="pendingNear" class="flex justify-center py-6">
                <BaseProgressSpinner stroke-width="4" />
            </div>
            <div
                v-else-if="buildings.length > 0"
                class="flex flex-col gap-1 max-h-80 overflow-y-auto"
            >
                <div
                    v-for="b in buildings"
                    :key="getBid(b)"
                    class="flex items-stretch gap-2 rounded border border-surface-200 p-2 hover:bg-surface-50 min-h-20"
                    @click.stop="onToggleItemBuilding(b)"
                >
                    <BaseCheckbox
                        :model-value="selectedId === getBid(b)"
                        binary
                        :input-id="`relation-parent-${getBid(b)}`"
                        class="shrink-0 self-center"
                        @click.stop
                        @update:model-value="
                            (v: boolean) => onSelectRow(getBid(b), v)
                        "
                    />
                    <ItemBuilding
                        class="min-w-0 flex-1"
                        :building="toItemBuildingData(b)"
                        :show-tags="false"
                        @click.stop="onToggleItemBuilding(b)"
                    />
                </div>
            </div>
            <p v-else class="text-center text-sm text-surface-600">
                {{ t("empty") }}
            </p>
            <BaseMessage v-if="selectedId" severity="warn" :closable="false">
                {{ t("warning") }}
            </BaseMessage>
            <BaseMessage v-if="errorMessage" severity="error" :closable="false">
                {{ errorMessage }}
            </BaseMessage>
            <div class="flex justify-end gap-2 pt-2">
                <BaseButton
                    :label="t('cancel')"
                    severity="secondary"
                    @click="onClose"
                />
                <BaseButton
                    :label="t('add')"
                    :loading="loadingConfirm"
                    :disabled="!selectedId || pendingNear"
                    @click="onConfirm"
                />
            </div>
        </div>
    </BaseDialog>
</template>
